// Client for the shop back-office admin endpoints (APA.ADMIN.API):
//   /api/orders, /api/customers, /api/core-returns, /api/shop/dashboard
//
// Goes through the shared `axios` instance so the Auth0AxiosBridge attaches the admin Bearer token
// (a raw fetch would be unauthenticated when BYPASS_AUTH is off). Mirrors the externalApiClient
// pattern. All money is integer cents; format with `eurCents` below.
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const base = `${apiBase}/api`;

function toError(e: any): Error {
	const data = e?.response?.data;
	const msg =
		(typeof data === "string" && data) ||
		data?.error ||
		data?.message ||
		e?.response?.statusText ||
		e?.message ||
		(e?.response?.status ? `HTTP ${e.response.status}` : "Erreur réseau");
	return new Error(typeof msg === "string" ? msg : "Erreur");
}

// ============================ Types ============================

export type OrderStatus = "Pending" | "Paid" | "Fulfilled" | "Refunded" | "Cancelled";

export interface ShopOrderListItem {
	id: string;
	status: OrderStatus;
	currency: string;
	totalCents: number;
	subtotalCents: number;
	consigneTotalCents: number;
	refundedTotalCents: number;
	customerEmail?: string | null;
	customerName?: string | null;
	userId?: string | null;
	createdAt: string;
	paidAt?: string | null;
	shippedAt?: string | null;
	itemCount: number;
}

export interface ShopOrderLine {
	id: string;
	productContentId?: string | null;
	title: string;
	reference?: string | null;
	sku?: string | null;
	kind: string; // "Product" | "Consigne"
	unitPriceCents: number;
	quantity: number;
	lineTotalCents: number;
}

export interface ShopCoreReturn {
	id: string;
	productContentId?: string | null;
	status: string;
	refundAmountCents: number;
	stripeRefundId?: string | null;
	receivedAt?: string | null;
	refundedAt?: string | null;
}

export interface ShopOrderEvent {
	id: string;
	type: string;
	message: string;
	actor?: string | null;
	createdAt: string;
}

export interface ShopOrderDetail {
	id: string;
	status: OrderStatus;
	currency: string;
	subtotalCents: number;
	consigneTotalCents: number;
	totalCents: number;
	refundedTotalCents: number;
	userId?: string | null;
	customerEmail?: string | null;
	customerName?: string | null;
	shippingName?: string | null;
	shippingLine1?: string | null;
	shippingLine2?: string | null;
	shippingPostalCode?: string | null;
	shippingCity?: string | null;
	shippingCountry?: string | null;
	trackingCarrier?: string | null;
	trackingNumber?: string | null;
	internalNotes?: string | null;
	cancelReason?: string | null;
	stripeSessionId: string;
	stripePaymentIntentId?: string | null;
	createdAt: string;
	paidAt?: string | null;
	shippedAt?: string | null;
	cancelledAt?: string | null;
	anonymizedAt?: string | null;
	lines: ShopOrderLine[];
	coreReturns: ShopCoreReturn[];
	events: ShopOrderEvent[];
}

export interface ShopAddress {
	name?: string | null;
	line1?: string | null;
	line2?: string | null;
	postalCode?: string | null;
	city?: string | null;
	country?: string | null;
}

export interface ShopCustomerListItem {
	id: string;
	email: string;
	name?: string | null;
	createdAt?: string | null;
	lastSignInAt?: string | null;
	confirmed: boolean;
	banned: boolean;
	ordersCount: number;
	totalSpentCents: number;
	isPro: boolean;
}

export interface ShopCustomerDetail {
	id: string;
	email: string;
	name?: string | null;
	phone?: string | null;
	createdAt?: string | null;
	lastSignInAt?: string | null;
	confirmed: boolean;
	banned: boolean;
	ordersCount: number;
	totalSpentCents: number;
	isPro: boolean;
	company?: string | null;
	vatNumber?: string | null;
	orders: ShopOrderListItem[];
	addresses: ShopAddress[];
	metadata: Record<string, string>;
}

// ---- Stock ----
export interface ShopStockItem {
	contentId: string;
	title: string;
	reference?: string | null;
	sku?: string | null;
	family?: string | null;
	quantity?: number | null;
	stockStatus?: string | null;
	priceCents: number;
	disabled: boolean;
	needsRedeploy: boolean;
}
export interface ShopStockKpis {
	totalSkus: number;
	outOfStock: number;
	lowStock: number;
	untracked: number;
	unitsOnHand: number;
	stockValueCents: number;
}
export interface ShopStockAdjustment {
	id: string;
	oldQuantity?: number | null;
	newQuantity?: number | null;
	delta: number;
	reason: string;
	orderId?: string | null;
	actor?: string | null;
	createdAt: string;
}

// ---- Quotes (devis) ----
export type QuoteStatus = "New" | "Quoted" | "Won" | "Lost";
export interface ShopQuoteLine { id: string; productRef: string; productContentId?: string | null; productTitle?: string | null; quantity: number; note?: string | null; }
export interface ShopQuoteListItem {
	id: string;
	status: QuoteStatus;
	company?: string | null;
	customerName?: string | null;
	customerEmail?: string | null;
	vatNumber?: string | null;
	lineCount: number;
	quotedTotalCents?: number | null;
	createdAt: string;
}
export interface ShopQuoteDetail {
	id: string;
	status: QuoteStatus;
	customerEmail?: string | null;
	customerName?: string | null;
	company?: string | null;
	vatNumber?: string | null;
	phone?: string | null;
	userId?: string | null;
	message?: string | null;
	adminNotes?: string | null;
	quotedTotalCents?: number | null;
	createdAt: string;
	updatedAt: string;
	lines: ShopQuoteLine[];
}

export interface ShopCoreReturnRow {
	id: string;
	orderId: string;
	customerEmail?: string | null;
	productTitle?: string | null;
	status: string;
	refundAmountCents: number;
	currency: string;
	stripeRefundId?: string | null;
	createdAt: string;
	receivedAt?: string | null;
	refundedAt?: string | null;
}

export interface ShopStatusCount { status: string; count: number; }
export interface ShopTopProduct { title: string; productContentId?: string | null; quantity: number; revenueCents: number; }
export interface ShopDailyPoint { date: string; orders: number; revenueCents: number; }

export interface ShopDashboardStats {
	revenueTotalCents: number;
	revenue30dCents: number;
	ordersTotal: number;
	ordersPaid: number;
	ordersFulfilled: number;
	ordersAwaitingFulfilment: number;
	ordersCancelled: number;
	ordersRefunded: number;
	averageOrderValueCents: number;
	customersCount: number;
	consigneAwaitingCount: number;
	consigneAwaitingCents: number;
	unitsSold30d: number;
	stock: ShopStockKpis;
	salesTrend: ShopDailyPoint[];
	statusBreakdown: ShopStatusCount[];
	recentOrders: ShopOrderListItem[];
	topProducts: ShopTopProduct[];
	topProductsByUnits: ShopTopProduct[];
}

export interface OrderListParams {
	status?: string;
	q?: string;
	from?: string;
	to?: string;
	page?: number;
	limit?: number;
}

// ============================ Helpers ============================

/** Integer cents → "1 234,56 €" (or other 3-letter currency). */
export function eurCents(cents: number, currency = "EUR"): string {
	const sym = currency.toUpperCase() === "EUR" ? "€" : currency.toUpperCase();
	return (
		(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
		" " +
		sym
	);
}

export function fmtDate(s?: string | null): string {
	if (!s) return "—";
	const d = new Date(s);
	return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(s?: string | null): string {
	if (!s) return "—";
	const d = new Date(s);
	return isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
}

// ============================ Client ============================

export const shopClient = {
	orders: {
		list: async (p: OrderListParams = {}): Promise<{ items: ShopOrderListItem[]; total: number }> => {
			try {
				const r = await axios.get(`${base}/orders`, { params: p, withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		get: async (id: string): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.get<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		setStatus: async (id: string, status: OrderStatus): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.patch<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}/status`, { status }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		ship: async (id: string, carrier?: string, trackingNumber?: string): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.post<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}/ship`, { carrier, trackingNumber }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		cancel: async (id: string, reason?: string): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.post<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}/cancel`, { reason }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		note: async (id: string, note: string): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.post<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}/note`, { note }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		refund: async (id: string, amountCents?: number | null): Promise<ShopOrderDetail> => {
			try {
				const r = await axios.post<ShopOrderDetail>(`${base}/orders/${encodeURIComponent(id)}/refund`, { amountCents: amountCents ?? null }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
	},

	customers: {
		list: async (q?: string, page = 1, perPage = 50): Promise<{ items: ShopCustomerListItem[]; total: number; hasMore: boolean; available: boolean }> => {
			try {
				const r = await axios.get(`${base}/customers`, { params: { q, page, perPage }, withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		get: async (id: string): Promise<ShopCustomerDetail> => {
			try {
				const r = await axios.get<ShopCustomerDetail>(`${base}/customers/${encodeURIComponent(id)}`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		export: async (id: string): Promise<unknown> => {
			try {
				const r = await axios.get(`${base}/customers/${encodeURIComponent(id)}/export`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		remove: async (id: string): Promise<{ deleted: boolean; anonymisedOrders: number }> => {
			try {
				const r = await axios.delete(`${base}/customers/${encodeURIComponent(id)}`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		setPro: async (id: string, isPro: boolean, company?: string, vatNumber?: string): Promise<ShopCustomerDetail> => {
			try {
				const r = await axios.post<ShopCustomerDetail>(`${base}/customers/${encodeURIComponent(id)}/pro`, { isPro, company, vatNumber }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
	},

	stock: {
		list: async (filter?: string, q?: string): Promise<{ items: ShopStockItem[]; kpis: ShopStockKpis }> => {
			try {
				const r = await axios.get(`${base}/stock`, { params: { filter, q }, withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		set: async (id: string, quantity: number, reason?: string): Promise<ShopStockItem> => {
			try {
				const r = await axios.post<ShopStockItem>(`${base}/stock/${encodeURIComponent(id)}/set`, { quantity, reason }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		adjust: async (id: string, delta: number, reason?: string): Promise<ShopStockItem> => {
			try {
				const r = await axios.post<ShopStockItem>(`${base}/stock/${encodeURIComponent(id)}/adjust`, { delta, reason }, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		history: async (id: string): Promise<ShopStockAdjustment[]> => {
			try {
				const r = await axios.get<ShopStockAdjustment[]>(`${base}/stock/${encodeURIComponent(id)}/history`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
	},

	quotes: {
		list: async (status?: string, q?: string, page = 1, perPage = 50): Promise<{ items: ShopQuoteListItem[]; total: number }> => {
			try {
				const r = await axios.get(`${base}/quotes`, { params: { status, q, page, perPage }, withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		get: async (id: string): Promise<ShopQuoteDetail> => {
			try {
				const r = await axios.get<ShopQuoteDetail>(`${base}/quotes/${encodeURIComponent(id)}`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		update: async (id: string, patch: { status?: string; adminNotes?: string; quotedTotalCents?: number | null }): Promise<ShopQuoteDetail> => {
			try {
				const r = await axios.patch<ShopQuoteDetail>(`${base}/quotes/${encodeURIComponent(id)}`, patch, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
	},

	returns: {
		list: async (status?: string, page = 1, limit = 100): Promise<{ items: ShopCoreReturnRow[]; total: number }> => {
			try {
				const r = await axios.get(`${base}/core-returns`, { params: { status, page, limit }, withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
		receive: async (id: string): Promise<void> => {
			try { await axios.post(`${base}/core-returns/${encodeURIComponent(id)}/receive`, {}, { withCredentials: true }); }
			catch (e) { throw toError(e); }
		},
		refund: async (id: string): Promise<void> => {
			try { await axios.post(`${base}/core-returns/${encodeURIComponent(id)}/refund`, {}, { withCredentials: true }); }
			catch (e) { throw toError(e); }
		},
		forfeit: async (id: string): Promise<void> => {
			try { await axios.post(`${base}/core-returns/${encodeURIComponent(id)}/forfeit`, {}, { withCredentials: true }); }
			catch (e) { throw toError(e); }
		},
	},

	dashboard: {
		stats: async (): Promise<ShopDashboardStats> => {
			try {
				const r = await axios.get<ShopDashboardStats>(`${base}/shop/dashboard/stats`, { withCredentials: true });
				return r.data;
			} catch (e) { throw toError(e); }
		},
	},
};
